import { useState, type ReactNode } from 'react';
import { arrivalBasisOf, delayQuestion, legalBranch, type DelayQuestionReason } from '@delaybuddy/contract';
import { useNextStep } from '../../app/navigation';
import { Screen, Sheet } from '../../chrome';
import {
  Button,
  Hint,
  Note,
  PickList,
  Question,
  Spinner,
  Sub,
  TextAreaField,
  TextLink,
  type PickOption,
} from '../../components';
import { useT, type T } from '../../i18n';
import { useTrack } from '../../lib/analytics';
import {
  useSetLegalAnswer,
  type ArrivalDelayBand,
  type CancellationNotice,
  type Enquiry,
  type Flight,
  type LegalAnswer,
  type LegalBranch,
} from '../../lib/api';
import { StageError, TripSummary } from '../S1/stage';
import { useStage } from '../S1/trip';
import { FlightDataCredit } from '../S3/Credit';
import { humanMins } from '../S3/flight';
import './S6.css';

/** `unsure`: "I don't know, but I believe it was 3 hours or more" (1.4, decision 145). */
type Answer = ArrivalDelayBand | 'unsure' | CancellationNotice | 'no' | 'yes';

/** The contract's `DelayNote` limit. */
const NOTE_MAX = 1000;

/** The answer already given on this branch, as the pick's value (a revisit shows it). */
function previous(a: LegalAnswer | null, branch: LegalBranch): Answer | null {
  if (!a || a.branch !== branch) return null;
  if (a.branch === 'cancelled') return a.notice;
  if (a.branch === 'denied_boarding') return a.volunteered === null ? null : a.volunteered ? 'yes' : 'no';
  return a.arrivalUnsure ? 'unsure' : a.arrival;
}

/**
 * The answer as the API takes it. 1.4: "unsure" is `3h_or_more` + `arrivalUnsure` (not a band of
 * its own), and the note is `delayNote`. Both are sent only when given, so a sure answer without a
 * note is exactly what a 1.3 API knows.
 */
function toAnswer(branch: LegalBranch, v: Answer, note: string): LegalAnswer {
  if (branch === 'cancelled') return { branch, notice: v as CancellationNotice };
  if (branch === 'denied_boarding') return { branch, hadDocuments: true, volunteered: v === 'yes' };
  const delayNote = note.trim() ? { delayNote: note.trim() } : {};
  if (v === 'unsure') return { branch, arrival: '3h_or_more', arrivalUnsure: true, ...delayNote };
  return { branch, arrival: v as ArrivalDelayBand, ...delayNote };
}

/** The final flight: the delay is counted at the final destination. */
const finalFlight = (e: Enquiry): Flight | null => e.legs[e.legs.length - 1]?.flight ?? null;

/**
 * Why S6 asks (the resolver's `delayQuestion`), in one line. Null: nothing to add (gate data
 * proved it, but they told us on S4 themselves).
 */
function whyLine(t: T, reason: DelayQuestionReason | null, f: Flight | null): string | null {
  switch (reason) {
    case 'connection':
      return t('S6.missed.sub');
    case 'no_gate_data':
      return f && arrivalBasisOf(f) === 'runway' ? t('S6.why.landed') : t('S6.why.noData');
    case 'disputed':
    case 'gate_under_3h':
      return t('S6.why.yourView');
    default:
      return null;
  }
}

/**
 * What our data shows when it says under 3 hours, plainly and without arguing (decision 146: the
 * answer decides): the final flight's gate or touchdown delay. Nothing when the data has no
 * such figure, or proves 3 hours anyway.
 */
function dataLine(t: T, f: Flight | null): string | null {
  if (!f || f.arrivalDelayMin === null) return null;
  const basis = arrivalBasisOf(f);
  if (basis !== 'gate' && basis !== 'runway') return null;
  const m = f.arrivalDelayMin;
  /* Only when it says under 3 hours: 3 hours or more by the data is accepted on the data. */
  if (m >= 180) return null;
  if (basis === 'runway')
    return m > 0 ? t('S6.data.landed', { delay: humanMins(t, m) }) : t('S6.data.landedOnTime');
  return m > 0 ? t('S6.data.gate', { delay: humanMins(t, m) }) : t('S6.data.gateOnTime');
}

/**
 * S6 · the legal question, one screen, branch by disruption type (the contract's
 * `legalBranch`). Denied boarding opens with the documents sheet — the funnel's only
 * pre-lead deflection (No → S6-stop). The verdict is computed now, shown after S7.
 */
export default function ScreenS6() {
  const t = useT();
  const { enquiryId, enquiry, trip, isLoading, error } = useStage('S6');
  return (
    <S6Body
      key={enquiry ? legalBranch(enquiry) : 'loading'}
      enquiryId={enquiryId}
      enquiry={enquiry}
      loading={isLoading || !enquiry}
      error={error}
      summary={<TripSummary enquiry={enquiry} trip={trip} editTo="S3" />}
      title={t('S6.title')}
    />
  );
}

function S6Body({
  enquiryId,
  enquiry,
  loading,
  error,
  summary,
  title,
}: {
  enquiryId: string | null;
  enquiry: Enquiry | undefined;
  loading: boolean;
  error: unknown;
  summary: ReactNode;
  title: string;
}) {
  const t = useT();
  const track = useTrack();
  const setLegal = useSetLegalAnswer(enquiryId);
  const { goNext } = useNextStep();
  const branch = enquiry ? legalBranch(enquiry) : null;
  const [picked, setPicked] = useState<Answer | null>(null);
  const earlier = enquiry && branch ? previous(enquiry.legalAnswer, branch) : null;
  const earlierNote =
    enquiry?.legalAnswer && 'arrival' in enquiry.legalAnswer ? (enquiry.legalAnswer.delayNote ?? '') : '';
  const [note, setNote] = useState(earlierNote);
  /* An answer from before the long-haul split ("3 hours or more") isn't one of the options now. */
  const stale = earlier === '3h_or_more' && enquiry?.quote?.band === 'long';
  const value = picked ?? (stale ? null : earlier);
  const hadDocs =
    enquiry?.legalAnswer?.branch === 'denied_boarding' && enquiry.legalAnswer.hadDocuments === true;
  const [gatePassed, setGatePassed] = useState(hadDocs);

  async function send(answer: LegalAnswer) {
    track('s6_answered', {
      branch: answer.branch,
      /* Never the note itself: only whether there is one. */
      ...('arrival' in answer ? { withNote: !!answer.delayNote } : {}),
      answer:
        'arrival' in answer
          ? answer.arrivalUnsure
            ? 'unsure_3h_or_more'
            : answer.arrival
          : 'notice' in answer
            ? answer.notice
            : answer.hadDocuments
              ? `volunteered_${answer.volunteered}`
              : 'no_documents',
    });
    const e = await setLegal.mutateAsync(answer);
    await goNext(e);
  }
  const onSend = (a: LegalAnswer) => void send(a).catch(() => {});

  if (error)
    return (
      <Screen id="S6" title={title}>
        <StageError />
      </Screen>
    );
  if (loading || !enquiry || !branch)
    return (
      <Screen id="S6" title={title}>
        <Spinner />
      </Screen>
    );

  const destination = enquiry.trip.destination;
  const where = { city: destination?.city ?? '', iata: destination?.iata ?? '' };
  /* Q39 (w5-06): on a long-haul flight (the band 3–4 h halves) "3 hours or more" is split. */
  const longHaul = enquiry.quote?.band === 'long';
  /* 1.4 (decision 145): "I don't know, but I believe it was 3 hours or more", with the bands. On
     long haul it is still `3h_or_more` + `arrivalUnsure`, which the API pays in full by default:
     DECISION-NEEDED (w8-01, Anton), so no copy here names an amount for it. */
  const delayOptions: PickOption<Answer>[] = [
    ...(longHaul
      ? [
          { value: '4h_or_more' as const, label: t('S6.delay.4hOrMore') },
          { value: '3h_to_4h' as const, label: t('S6.delay.3to4h') },
        ]
      : [{ value: '3h_or_more' as const, label: t('S6.delay.3h') }]),
    { value: 'unsure', label: t('S6.delay.unsure') },
    { value: 'under_3h', label: t('S6.delay.under3h') },
    { value: 'never_arrived', label: t('S6.delay.never') },
  ];
  const isDelay = branch === 'delayed' || branch === 'missed_connection';
  const final = finalFlight(enquiry);
  const why = isDelay ? whyLine(t, delayQuestion(enquiry), final) : null;
  /* The data line belongs with a reason to ask; on a missed connection the final flight's own
     figure isn't the question (they may not have been on it). */
  const shows = isDelay && branch === 'delayed' ? dataLine(t, final) : null;
  const q = {
    delayed: { question: t('S6.delayed.q', where), sub: why, options: delayOptions },
    missed_connection: {
      question: t('S6.missed.q', where),
      sub: why ?? t('S6.missed.sub'),
      options: delayOptions,
    },
    cancelled: {
      question: t('S6.cancelled.q'),
      sub: null,
      options: [
        { value: 'less_than_14_days', label: t('S6.cancelled.lt14') },
        { value: '14_days_or_more', label: t('S6.cancelled.ge14') },
        { value: 'not_told', label: t('S6.cancelled.notTold') },
      ] as PickOption<Answer>[],
    },
    // Rule: "No" first (ux-flow.md S6).
    denied_boarding: {
      question: t('S6.denied.q'),
      sub: null,
      options: [
        { value: 'no', label: t('S6.denied.no') },
        { value: 'yes', label: t('S6.denied.yes') },
      ] as PickOption<Answer>[],
    },
  }[branch];

  return (
    <Screen
      id="S6"
      title={q.question}
      dock={
        <Button
          onClick={() => value && onSend(toAnswer(branch, value, note))}
          disabled={!value || setLegal.isPending}
        >
          {t('common.continue')}
        </Button>
      }
    >
      {summary}
      <Question>{q.question}</Question>
      {q.sub && <Sub>{q.sub}</Sub>}
      {isDelay && (
        <div className="s6-how">
          <p className="s6-how__body">{t('S6.delay.how')}</p>
          {shows && (
            <p className="s6-how__data">
              {shows} {t('S6.data.decides')}
              <FlightDataCredit flight={final} className="s6-how__credit" />
            </p>
          )}
        </div>
      )}
      <PickList options={q.options} value={value} onChange={setPicked} label={q.question} />
      {longHaul && isDelay && <Note>{t('S6.delay.longHaulNote')}</Note>}
      {isDelay && (
        <TextAreaField
          label={t('S6.note.label')}
          placeholder={t('S6.note.placeholder')}
          value={note}
          maxLength={NOTE_MAX}
          rows={3}
          onChange={(e) => setNote(e.target.value)}
        />
      )}
      {setLegal.isError && <Hint warn>{t('S6.saveFailed')}</Hint>}

      <Sheet open={branch === 'denied_boarding' && !gatePassed} title={t('S6.gate.title')}>
        <Note>{t('S6.gate.note')}</Note>
        <Button
          onClick={() => {
            track('s6_docgate', { hadDocuments: true });
            setGatePassed(true);
          }}
        >
          {t('S6.gate.yes')}
        </Button>
        <TextLink
          disabled={setLegal.isPending}
          onClick={() => {
            track('s6_docgate', { hadDocuments: false });
            onSend({ branch: 'denied_boarding', hadDocuments: false, volunteered: null });
          }}
        >
          {t('S6.gate.no')}
        </TextLink>
      </Sheet>
    </Screen>
  );
}
